import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, Stepper, StoneStepper } from '../shared';
import { formatNum, formatPercent } from '../../utils/helpers';

const STORAGE_KEY = 'augment_calc_last';
const TOTAL_SKILLS = 178;
const STONE_CHANCES = {
  life: 0.01,
  mid: 0.06,
  top: 0.25,
  ultimate: 1.0
};

export function AugmentPanel({ active }) {
  const { t, lang, getPlural } = useLanguage();
  
  const [skillCount, setSkillCount] = useState(0);
  const [stoneCounts, setStoneCounts] = useState({
    life: 0,
    mid: 0,
    top: 0,
    ultimate: 0
  });
  const [topPrice, setTopPrice] = useState(2500);
  
  // Restore state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.skillCount !== undefined) setSkillCount(parseInt(state.skillCount) || 0);
        if (state.lifeCount !== undefined) setStoneCounts(prev => ({ ...prev, life: parseInt(state.lifeCount) || 0 }));
        if (state.midCount !== undefined) setStoneCounts(prev => ({ ...prev, mid: parseInt(state.midCount) || 0 }));
        if (state.topCount !== undefined) setStoneCounts(prev => ({ ...prev, top: parseInt(state.topCount) || 0 }));
        if (state.ultimateCount !== undefined) setStoneCounts(prev => ({ ...prev, ultimate: parseInt(state.ultimateCount) || 0 }));
        if (state.topPrice) setTopPrice(parseInt(state.topPrice) || 2500);
      }
    } catch (e) {}
  }, []);
  
  // Save state to localStorage
  useEffect(() => {
    try {
      const state = {
        skillCount: skillCount.toString(),
        lifeCount: stoneCounts.life.toString(),
        midCount: stoneCounts.mid.toString(),
        topCount: stoneCounts.top.toString(),
        ultimateCount: stoneCounts.ultimate.toString(),
        topPrice: topPrice.toString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }, [skillCount, stoneCounts, topPrice]);

  // Calculate prices based on top price (conversion rates)
  const prices = {
    life: topPrice / 20,
    mid: topPrice / 4,
    top: topPrice,
    ultimate: topPrice * 4
  };

  // Calculate single chances and expected attempts
  const n = Math.max(0, Math.min(178, skillCount));
  const singleChances = {};
  const expectedAttempts = {};
  
  for (const [stone, stoneChance] of Object.entries(STONE_CHANCES)) {
    singleChances[stone] = n > 0 ? stoneChance * (n / TOTAL_SKILLS) : 0;
    expectedAttempts[stone] = singleChances[stone] > 0 ? Math.ceil(1 / singleChances[stone]) : Infinity;
  }

  // Calculate combined probability
  let probNone = 1;
  let totalAttempts = 0;
  
  for (const [stone, count] of Object.entries(stoneCounts)) {
    if (count > 0 && singleChances[stone] > 0) {
      probNone *= Math.pow(1 - singleChances[stone], count);
      totalAttempts += count;
    }
  }
  
  const finalChance = 1 - probNone;

  const handleStoneChange = useCallback((stone, value) => {
    setStoneCounts(prev => ({ ...prev, [stone]: value }));
  }, []);

  const showDetails = n > 0;
  const showProbability = totalAttempts > 0;
  const probabilityLabel = n === 1
    ? t('probabilityOfSpecificSuccess')
    : t('probabilityOfSuccess', { n });

  // Build augment text
  let augmentText = '';
  if (lang === 'ru') {
    augmentText = getPlural(totalAttempts, 'попыткой', 'попытками', 'попытками');
  } else if (lang === 'ua') {
    augmentText = getPlural(totalAttempts, 'спробою', 'спробами', 'спробами');
  } else {
    augmentText = totalAttempts === 1 ? t('totalAugmentation') : t('totalAugmentationsPlural');
  }

  if (!active) return null;

  return (
    <div className="calculator-panel active">
      <Card title={t('augmentConfig')}>
        <div className="input-group">
          <label>{t('desiredSkills')}</label>
          <Stepper
            value={skillCount}
            onChange={setSkillCount}
            min={0}
            max={178}
          />
        </div>
        <div className="input-group">
          <label>{t('topGradePrice')}</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={topPrice}
            onChange={(e) => setTopPrice(parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
            onBlur={() => setTopPrice(prev => Math.max(1, prev))}
            onFocus={(e) => {
              const input = e.target;
              requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
            }}
          />
        </div>
      </Card>

      {showDetails && (
        <>
          <div className="aug-results">
            <div className="aug-results-title">✦ {t('analysis')} ✦</div>

            <div className="result-section">
              <h3>{t('singleAugmentSuccess')}</h3>
              <div className="prob-grid">
                {['life', 'mid', 'top', 'ultimate'].map(stone => (
                  <div key={stone} className={`prob-item ${stone}`}>
                    <div className="label">{t(`stone${stone.charAt(0).toUpperCase() + stone.slice(1)}`)}</div>
                    <div className="value">{formatPercent(singleChances[stone])}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="result-section">
              <h3>{t('expectedAttemptsNeeded')}</h3>
              <div className="prob-grid">
                {['life', 'mid', 'top', 'ultimate'].map(stone => (
                  <div key={stone} className={`prob-item ${stone}`}>
                    <div className="label">{t(`stone${stone.charAt(0).toUpperCase() + stone.slice(1)}`)}</div>
                    <div className="value">
                      {expectedAttempts[stone] === Infinity ? '∞' : expectedAttempts[stone].toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="result-section">
              <h3>{t('expectedCostAdena')}</h3>
              <div className="prob-grid">
                {['life', 'mid', 'top', 'ultimate'].map(stone => (
                  <div key={stone} className={`prob-item ${stone}`}>
                    <div className="label">{t(`stone${stone.charAt(0).toUpperCase() + stone.slice(1)}`)}</div>
                    <div className="value">
                      ~{expectedAttempts[stone] === Infinity ? '∞' : formatNum(expectedAttempts[stone] * prices[stone])}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Card title={t('lifeStones')}>
            <div className="stone-grid">
              <div className="stone-row life">
                <div className="stone-info">
                  <span className="stone-name">
                    <span className="stone-icon">◆</span>
                    Life Stone
                  </span>
                  <span className="stone-chance">{t('skillChance1')}</span>
                </div>
                <StoneStepper
                  value={stoneCounts.life}
                  onChange={(v) => handleStoneChange('life', v)}
                />
              </div>

              <div className="stone-row mid">
                <div className="stone-info">
                  <span className="stone-name">
                    <span className="stone-icon">◆</span>
                    Mid-Grade Life Stone
                  </span>
                  <span className="stone-chance">{t('skillChance6')}</span>
                </div>
                <StoneStepper
                  value={stoneCounts.mid}
                  onChange={(v) => handleStoneChange('mid', v)}
                />
              </div>

              <div className="stone-row top">
                <div className="stone-info">
                  <span className="stone-name">
                    <span className="stone-icon">◆</span>
                    Top-Grade Life Stone
                  </span>
                  <span className="stone-chance">{t('skillChance25')}</span>
                </div>
                <StoneStepper
                  value={stoneCounts.top}
                  onChange={(v) => handleStoneChange('top', v)}
                />
              </div>

              <div className="stone-row ultimate">
                <div className="stone-info">
                  <span className="stone-name">
                    <span className="stone-icon">★</span>
                    Ultimate Life Stone
                  </span>
                  <span className="stone-chance">{t('skillChance100')}</span>
                </div>
                <StoneStepper
                  value={stoneCounts.ultimate}
                  onChange={(v) => handleStoneChange('ultimate', v)}
                />
              </div>
            </div>

            {showProbability && (
              <div className="inventory-probability">
                <div className="probability-content">
                  <div className="probability-label">{probabilityLabel}</div>
                  <div className="probability-value">{formatPercent(finalChance)}</div>
                  <div className="probability-subtext">
                    {t('withTotalAugmentations')} {totalAttempts.toLocaleString()} {augmentText}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
